const corporateContentData = {
  heroHeading: 'New Work City is an independent ideas lab operating somewhere between agency and anomaly.',
  sections: [
    {
      id: 'who-is-new-work-city',
      title: 'WHO IS NEW WORK CITY',
      type: 'standard',
      delayUntilHero: true,
      contentBlocks: [
        {
          type: 'paragraph',
          html: "New Work City works at the intersection of brand, culture, and creative technology (mainly because that's where the wifi is the strongest). We exist to <strong>radically reimagine</strong> the way brands are built, told, and experienced - preferably before lunch."
        },
        {
          type: 'paragraph',
          html: 'We create remarkable products, experiences, and stories – sometimes for ourselves, sometimes for clients. <strong>Always for impact.</strong>'
        },
        {
          type: 'contentHeading',
          html: 'Who is New Work City?'
        },
        {
          type: 'paragraph',
          html: "We're a collective of strategists, specialists, and mischief-makers who believe the future belongs to those willing to question everything."
        },
        {
          type: 'paragraph',
          html: "We work with ambitious people behind brands to break patterns, challenge norms, and prototype new realities that can't be ignored."
        },
        {
          type: 'paragraph',
          html: "Our process is half science, half séance. We blend rigorous strategy with reckless creativity, turning data into direction and chaos into coherence. We don't just make things look interesting. We make them matter."
        },
        {
          type: 'paragraph',
          html: "We're allergic to dull, motivated by impact, and powered by the strange chemistry that happens when diverse minds collide with impossible problems, and dexies."
        },
        {
          type: 'paragraph',
          html: "Sometimes you have to break something in order to build something better. So therefore, each project is a live experiment. Fire anyone that tells you it's not."
        }
      ]
    },
    {
      id: 'who-are-you-really',
      title: 'WHO ARE YOU REALLY',
      type: 'standard',
      contentBlocks: [
        {
          type: 'paragraph',
          html: "Officially, we're a studio."
        },
        {
          type: 'paragraph',
          html: "Unofficially, we're a government agency for the unlawful advancement of ideas."
        },
        {
          type: 'paragraph',
          html: "We're the people who got tired of \"best practice,\" so we built a place for <em>next</em> practice. A network where strategy thinks like art, art acts like code, and brands behave unexpectedly including our own."
        },
        {
          type: 'paragraph',
          html: "New Work City isn't a company you hire; it's a psychological experiment you consent to by scrolling. It's a service for people who want meaning but aren't afraid of mischief. We're the polite anarchy before the acquisition."
        },
        {
          type: 'paragraph',
          html: "In a new city, you can be anything, and those that stand out are the ones that are celebrated… so, what if you did something that no one else would do, to become something no one else could be?"
        },
        {
          type: 'paragraph',
          html: "Let's find out and commit several minor creative offences along the way."
        }
      ]
    },
    {
      id: 'values',
      title: "SOME THINGS WE'RE PASSIONATE ABOUT",
      type: 'values',
      items: [
        {
          title: 'Behave lawlessly',
          description:
            "We write our own rules and occasionally our own HR policies. We challenge 'best practice' conventions in favour of subversive thinking and slightly illegal metaphors. What rules are you following that were written by someone in a worse mood than you?"
        },
        {
          title: 'Commit to vision',
          description:
            "We prioritise taste over tools, vision over briefs, and conviction over committee notes. Be radically committed to the idea - especially when it scares your compliance team. When did you last protect a crazy idea instead of suffocating it with feedback?"
        },
        {
          title: 'Build new realities',
          description:
            "Whether it's a product, brand, or experience - everything we touch should provoke, change perspective, capture people's imagination, or make at least one stakeholder sweat. Are you creating what's next, or simply perfecting what's already been done?"
        },
        {
          title: 'Put play to work',
          description:
            'Imagination is our most powerful currency in NWC. We value experimentation, happy accidents, and ideas that frighten committees. When did you last make something just because it excited you?'
        },
        {
          title: 'Move freely.',
          description:
            "We liberate ourselves and our clients from unnecessary blockers, approval chains, and fear-based leadership. Motion is our method. What would you do differently if you weren't worried about the approval of Steve from finance?"
        }
      ]
    },
    {
      id: 'services',
      title: 'SERVICES',
      type: 'services',
      categories: [
        {
          title: 'Strategy / Reality Distortion',
          paragraphs: [
            "We specialise in shifting perspective - sometimes even on purpose. Our strategy team helps brands see themselves and their audiences differently, occasionally using mirrors and hypnosis. We don't sell decks; we sell perspective. Laminated for your protection."
          ],
          services: [
            'Brand + communications strategy',
            'Positioning + narrative architecture',
            'Campaign planning + go-to-market frameworks',
            'Competitive + cultural intelligence'
          ],
          listDelay: 0.2
        },
        {
          title: 'Design / Worldbuilding',
          paragraphs: [
            "We don't make logos. We build universes. Each design system is a living organism - occasionally aggressive, always hungry.",
            "Our goal is to direct visuals that behave like brands, not just look like them. If your identity doesn't give someone an existential crisis, did we even design it?",
            'Every pixel, a provocation.'
          ],
          services: [
            'Identity systems + visual languages',
            'Experience design + creative direction',
            'Art direction + spatial storytelling',
            'Brand evolution + guardianship'
          ],
          listDelay: 0.7
        },
        {
          title: 'Content/ Transmission',
          paragraphs: [
            'Stories are signals. Most brands broadcast noise.',
            'We craft transmissions that cut through - mixing narrative craft with cultural subtext, emotion, and a touch of chaos.',
            "From scripts to campaigns, from motion to myth-making, we work where creative meets consequence.",
            "Our work doesn't fill feeds - it feeds rumours (and existential dread in your competitors)."
          ],
          services: [
            'Campaign concepts + storytelling',
            'Scriptwriting + brand films',
            'Tone of voice + copy systems',
            'Episodic + branded content'
          ],
          listDelay: 1.3
        },
        {
          title: 'Experimentation / Broadcast',
          paragraphs: [
            'Some ideas deserve a lab coat. Others, a restraining order. Our experimental arm is where new ideas are tested, refined, and sometimes unleashed upon the public without sufficient warning.',
            "The future is in beta - and we're the ones pressing \"Run Anyway.\""
          ],
          services: [
            'R&D sprints + speculative design',
            'Media experiments + experiential activations',
            'Internal comms innovation + storytelling',
            'Branded entertainment + world simulation'
          ],
          listDelay: 1.7
        }
      ]
    },
    {
      id: 'ready-to-proceed',
      title: 'READY TO PROCEED',
      type: 'standard',
      contentBlocks: [
        {
          type: 'paragraph',
          html:
            "If this doesn't make you want to work with us, that's perfectly alright. It just means you're comfortable wasting your thirty-odd remaining summers before entropy wins. You've already squandered the first half on things you don't even talk about any more, so really, what's one more disappointment?"
        },
        {
          type: 'paragraph',
          html:
            '<strong>In New Work City, you can be anything - except ordinary. Ready to leave your comfort zone behind? Click here to proceed to creative non-compliance.</strong>'
        }
      ]
    },
    {
      id: 'contact',
      title: 'CONTACT US',
      type: 'contact',
      seoContactCopy:
        'Use the contact form to brief us on your next experiment, or email SAYHELLO@NW.CITY if you prefer keyboards to forms.'
    }
  ]
};

export default corporateContentData;

